<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="rounded-lg">
      <h3>{{ $t('ledger_year_title') }}</h3>
      <b-form-group :label="$t('ledger_year')">
        <b-form-select
          v-model.number="year"
          :options="yearOptions"
          class="ledger-year-select"
          @change="loadPreview"
        />
      </b-form-group>
      <div
        v-if="loading"
        class="text-center py-2"
      >
        <b-spinner />
      </div>
      <template v-else-if="preview">
        <b-alert
          v-for="(blocker, index) in preview.blockers || []"
          :key="index"
          show
          variant="warning"
        >
          {{ blockerText(blocker) }}
          <b-link
            v-if="blockerLink(blocker)"
            :to="blockerLink(blocker)"
          >
            {{ blockerLinkLabel(blocker) }}
          </b-link>
        </b-alert>
        <div class="d-flex flex-wrap mb-1">
          <div class="mr-2">
            {{ $t('ledger_year_revenue') }} <strong class="ledger-num">{{ money(preview.total_revenue) }}</strong>
          </div>
          <div class="mr-2">
            {{ $t('ledger_year_expenses') }} <strong class="ledger-num">{{ money(preview.total_expenses) }}</strong>
          </div>
          <div>
            {{ $t('ledger_year_net') }} <strong class="ledger-num">{{ money(preview.net_result) }}</strong>
          </div>
        </div>
        <b-card
          v-for="(branch, index) in preview.branches || []"
          :key="index"
          class="mb-1"
        >
          <h5>{{ branchTitle(branch) }}</h5>
          <div class="small mb-50">
            {{ $t('ledger_year_revenue') }}
            <span class="ledger-num">{{ money(branch.revenue) }}</span>
            · {{ $t('ledger_year_expenses') }}
            <span class="ledger-num">{{ money(branch.expenses) }}</span>
            · {{ $t('ledger_year_net') }}
            <span class="ledger-num">{{ money(branch.net_result) }}</span>
          </div>
          <div
            v-for="line in branch.journal || []"
            :key="line.gl_account_id + '-' + line.code"
            class="d-flex justify-content-between small"
          >
            <span>{{ line.code }}</span>
            <span class="ledger-num">{{ $t('ledger_debit') }} {{ money(line.debit) }} · {{ $t('ledger_credit') }} {{ money(line.credit) }}</span>
          </div>
        </b-card>
        <b-button
          v-if="canPost"
          variant="primary"
          class="rounded-pill"
          :disabled="acting || (preview.blockers || []).length > 0"
          @click="postClosing"
        >
          {{ $t('ledger_year_post') }}
        </b-button>
      </template>
    </b-card>

    <b-card
      v-if="closing"
      class="rounded-lg"
    >
      <div class="d-flex justify-content-between">
        <div>
          <h4>{{ $t('ledger_year') }} {{ closing.year }}</h4>
          <div>
            {{ $t('ledger_status') }}
            <b-badge>{{ enumLabel(closing.status, $i18n.locale) }}</b-badge>
          </div>
          <div>
            {{ $t('ledger_year_revenue') }}
            <span class="ledger-num">{{ money(closing.total_revenue) }}</span>
          </div>
          <div>
            {{ $t('ledger_year_expenses') }}
            <span class="ledger-num">{{ money(closing.total_expenses) }}</span>
          </div>
          <div>
            {{ $t('ledger_year_net') }}
            <span class="ledger-num">{{ money(closing.net_result) }}</span>
          </div>
          <div v-if="closing.posted_at">
            {{ $t('ledger_posted_at') }} {{ closing.posted_at }}
          </div>
          <div v-if="closing.reversed_at">
            {{ $t('ledger_year_reversed_at') }} {{ closing.reversed_at }}
          </div>
          <div v-if="closing.notes">
            {{ $t('ledger_notes') }} {{ closing.notes }}
          </div>
          <div v-if="closing.reversal_reason">
            {{ $t('ledger_year_reason') }} {{ closing.reversal_reason }}
          </div>
        </div>
        <b-button
          v-if="canReverse"
          variant="outline-danger"
          class="rounded-pill"
          @click="reverseOpen = true"
        >
          {{ $t('ledger_reverse') }}
        </b-button>
      </div>
      <div
        v-if="closing.journals && closing.journals.length"
        class="mt-1"
      >
        <div class="text-muted small">
          {{ $t('ledger_year_journals') }}
        </div>
        <div
          v-for="journal in closing.journals"
          :key="journal.id"
          class="mt-50"
        >
          <b-link :to="{ name: 'ledger-journals-view', params: { id: journal.id } }">
            {{ journal.entry_number || journal.id }}
          </b-link>
        </div>
      </div>
    </b-card>

    <b-modal
      v-model="reverseOpen"
      :title="$t('ledger_reverse')"
      hide-footer
    >
      <b-form-group :label="$t('ledger_year_reason')">
        <b-form-textarea
          v-model="reason"
          rows="3"
        />
      </b-form-group>
      <small
        v-if="reason.trim().length < 10"
        class="text-danger"
      >{{ $t('ledger_close_reason_min') }}</small>
      <b-button
        class="mt-1"
        variant="danger"
        :disabled="acting || reason.trim().length < 10"
        @click="reverseClosing"
      >
        {{ $t('ledger_reverse') }}
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BFormGroup, BFormSelect, BSpinner, BAlert, BLink, BButton, BBadge, BModal, BFormTextarea,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  previewYearClosing, createYearClosing, getYearClosing, reverseYearClosing,
} from '@/api/accounting/yearClosings'
import {
  oneFrom, enumLabel, formatLedgerMoney, apiErrorMessage,
} from '@/utils/ledger'

export default {
  name: 'YearClosings',
  components: {
    BCard, BFormGroup, BFormSelect, BSpinner, BAlert, BLink, BButton, BBadge, BModal, BFormTextarea,
  },
  data() {
    const current = new Date().getFullYear()
    return {
      year: Number(this.$route.query.year) || current - 1,
      preview: null,
      closing: null,
      loading: false,
      acting: false,
      reverseOpen: false,
      reason: '',
    }
  },
  computed: {
    yearOptions() {
      const current = new Date().getFullYear()
      const years = []
      for (let value = current; value >= current - 8; value -= 1) years.push(value)
      return years
    },
    canPost() {
      return this.hasPermission('post year_closings') && this.preview && !this.preview.already_closed
    },
    canReverse() {
      return this.hasPermission('reverse year_closings') && this.closing && this.statusValue === 'posted'
    },
    statusValue() {
      const status = this.closing && this.closing.status
      return status && typeof status === 'object' ? status.value : status
    },
  },
  mounted() {
    this.loadPreview()
  },
  methods: {
    enumLabel,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon', variant },
      })
    },
    branchTitle(branch) {
      if (!branch || branch.branch_id == null) return this.$t('ledger_year_company')
      return this.$t('ledger_year_branch', { id: branch.branch_id })
    },
    blockerText(blocker) {
      const keys = {
        already_closed: 'ledger_year_block_already_closed',
        year_not_ended: 'ledger_year_block_year_not_ended',
        open_periods: 'ledger_year_block_open_periods',
        previous_year_open: 'ledger_year_block_previous_year_open',
      }
      const key = keys[blocker && blocker.code]
      return key ? this.$t(key) : (blocker && blocker.message) || ''
    },
    blockerLinkLabel(blocker) {
      if (blocker && blocker.code === 'previous_year_open') return this.$t('ledger_year_open_year')
      return this.$t('ledger_year_open_periods')
    },
    blockerLink(blocker) {
      if (blocker.code === 'open_periods') return { name: 'ledger-periods', query: { year: this.year } }
      if (blocker.code === 'previous_year_open' && blocker.details && blocker.details.year) {
        return { name: 'ledger-year-closings', query: { year: blocker.details.year } }
      }
      return null
    },
    async loadPreview() {
      this.loading = true
      this.$router.replace({ query: { year: this.year } }).catch(() => {})
      try {
        const res = await previewYearClosing({ year: this.year })
        this.preview = oneFrom(res)
        if (this.preview && this.preview.closing_id) {
          const show = await getYearClosing(this.preview.closing_id)
          this.closing = oneFrom(show)
        } else {
          this.closing = null
        }
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    async postClosing() {
      this.acting = true
      try {
        const res = await createYearClosing({ year: this.year })
        this.closing = oneFrom(res)
        this.toast(this.$t('ledger_posted_success'), 'success')
        await this.loadPreview()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
        await this.loadPreview()
      } finally {
        this.acting = false
      }
    },
    async reverseClosing() {
      if (!this.closing) return
      this.acting = true
      try {
        const res = await reverseYearClosing(this.closing.id, { reason: this.reason.trim() })
        this.closing = oneFrom(res)
        this.reverseOpen = false
        this.reason = ''
        this.toast(this.$t('ledger_reversed_success'), 'success')
        await this.loadPreview()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.acting = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; }
.ledger-year-select { max-width: 10rem; }
</style>
